import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { supabase } from "@/lib/supabase";

interface Project {
  id: string;
  title: string;
  description: string;
  image_url: string;
  sector: string;
}

export default function ProjectsSection() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  useScrollAnimation();

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("id, title, description, image_url, sector")
      .order("created_at", { ascending: false })
      .limit(3);
    if (error) {
      console.error("Error fetching projects:", error);
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Re-observe scroll-animated elements after projects load
  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    const elements = document.querySelectorAll(".animate-on-scroll:not(.visible)");
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  return (
    <section id="projects" className="bg-stone-50 py-20 md:py-28 px-4 md:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14 animate-on-scroll">
          <span className="px-4 py-1.5 rounded-full bg-secondary text-primary text-xs font-inter font-semibold mb-5 tracking-wide uppercase inline-block">
            Our Projects
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-saria font-bold text-dark leading-tight max-w-2xl mx-auto">
            Learn more about what we do and get involved
          </h2>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
          </div>
        )}

        {!loading && projects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-stone-500">No projects available at the moment.</p>
          </div>
        )}

        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {projects.map((project, idx) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className={`animate-on-scroll animate-on-scroll-delay-${idx} group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-500 border border-stone-100 block`}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {/* Image */}
                <div className="relative h-[240px] overflow-hidden">
                  <img
                    src={project.image_url || `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28project.sector%29%7D%20humanitarian%20project%20in%20Cameroon%20community%20development%20warm%20natural%20light%20documentary%20style&width=800&height=500&seq=proj-fallback-${project.id}&orientation=landscape`}
                    alt={project.title}
                    title={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  {/* Category badge */}
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary text-white text-xs px-3 py-1.5 rounded-full font-inter font-semibold shadow-lg">
                      {project.sector}
                    </span>
                  </div>
                  {/* Hover overlay */}
                  <div
                    className={`absolute inset-0 bg-dark/60 flex items-center justify-center transition-all duration-500 ${
                      hoveredIdx === idx ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <span className="bg-white text-primary px-5 py-2.5 rounded-full text-sm font-inter font-semibold hover:bg-primary hover:text-white transition-all duration-300 whitespace-nowrap shadow-lg">
                      Read more
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg font-saria font-bold text-dark leading-snug mb-3 group-hover:text-primary transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-stone-500 text-sm leading-relaxed line-clamp-3">
                    {project.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}